import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2, ArrowRight, ArrowLeft, Building2, ClipboardList, Package, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  'Consumer Goods & Electronics',
  'Automotive',
  'Food & Beverages',
  '3PL, Logistics & Warehousing',
  'E & Q Commerce',
  'Pharmaceuticals',
  'Semi Conductors',
  'Manufacturing',
  'Aerospace / Aviation',
  'Other',
];

const warehouseSizes = [
  'Under 10,000 sq. ft.',
  '10,000 - 50,000 sq. ft.',
  '50,000 - 100,000 sq. ft.',
  '100,000 - 500,000 sq. ft.',
  'Over 500,000 sq. ft.',
];

const products = [
  { id: 'standard-racking', name: 'Standard Pallet Racking' },
  { id: 'adjustable-racking', name: 'Adjustable Pallet Racking' },
  { id: 'drive-in-racking', name: 'Drive-In Pallet Racking' },
  { id: 'shuttle-racking', name: 'Shuttle-Based Racking' },
  { id: 'asrs', name: 'ASRS Based Racking' },
  { id: 'mezzanine', name: 'Mezzanine Floors' },
  { id: 'material-handling', name: 'Material Handling Equipment' },
  { id: 'energy', name: 'Energy Efficiency Solutions' },
  { id: 'security', name: 'Security & IoT Systems' },
];

const steps = [
  { number: 1, title: 'Contact Info', icon: Building2, description: 'Your business details' },
  { number: 2, title: 'Requirements', icon: ClipboardList, description: 'Project specifications' },
  { number: 3, title: 'Products', icon: Package, description: 'Select solutions' },
];

export default function GetQuote() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    industry: '',
    warehouse_size: '',
    product_interest: [],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleProductToggle = (productId) => {
    const current = formData.product_interest;
    if (current.includes(productId)) {
      setFormData({ ...formData, product_interest: current.filter(id => id !== productId) });
    } else {
      setFormData({ ...formData, product_interest: [...current, productId] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      setIsSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.company_name && formData.contact_name && formData.email && formData.phone;
  const isStep2Valid = formData.industry && formData.warehouse_size;

  if (isSubmitted) {
    return (
      <div className="bg-[#0a1628] pt-20 min-h-screen" data-testid="quote-success">
        <div className="container-custom py-28 md:py-36">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center mx-auto mb-10">
              <Sparkles className="w-12 h-12 text-[#E63946]" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
              Thank You!
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed mb-10">
              Your quote request has been submitted successfully. Our team will review 
              your requirements and get back to you within <span className="text-white font-medium">24-48 hours</span> with 
              a customized proposal.
            </p>
            <div className="flex flex-wrap justify-center gap-5">
              <Link to="/">
                <Button className="btn-primary magnetic-btn clip-slant h-12 px-8">
                  Back to Home
                </Button>
              </Link>
              <Link to="/products">
                <Button className="btn-secondary magnetic-btn h-12 px-8">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a1628] pt-20" data-testid="get-quote-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#E63946] opacity-[0.02] blur-[200px] rounded-full" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
              // Get a Quote
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-[0.95] tracking-tight mb-6">
              Start Your <span className="gradient-text-blue">Infrastructure Journey</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              Tell us about your requirements and receive a customized quote within 24-48 hours. 
              No commitment required.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 md:py-24 bg-[#0c1e35]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Progress Steps */}
            <div className="mb-16">
              <div className="flex items-center justify-between relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-px bg-white/10" />
                <div 
                  className="absolute top-6 left-0 h-px bg-gradient-to-r from-[#E63946] to-[#E63946] transition-all duration-500"
                  style={{ width: `${((step - 1) / 2) * 100}%` }}
                />
                
                {steps.map((s) => (
                  <div key={s.number} className="relative flex flex-col items-center">
                    <div
                      className={`relative z-10 w-12 h-12 flex items-center justify-center border-2 transition-all duration-500 ${
                        step >= s.number
                          ? 'bg-[#E63946] border-[#E63946] text-white'
                          : 'bg-[#0c1e35] border-white/20 text-gray-600'
                      }`}
                    >
                      {step > s.number ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <s.icon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="mt-4 text-center">
                      <p className={`text-sm font-medium transition-colors ${step >= s.number ? 'text-white' : 'text-gray-600'}`}>
                        {s.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 hidden sm:block">{s.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="industrial-card bg-[#0a1628] border border-white/5 p-8 md:p-12">
              <form onSubmit={handleSubmit} data-testid="quote-form">
                {/* Step 1: Contact Info */}
                {step === 1 && (
                  <div className="space-y-8 animate-fade-in" data-testid="quote-step-1">
                    <div className="mb-10">
                      <h2 className="font-heading text-2xl font-bold text-white uppercase mb-2">
                        Contact Information
                      </h2>
                      <p className="text-gray-500">Let us know how to reach you</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Company Name *</label>
                        <Input
                          type="text"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          required
                          className="form-input h-13 rounded-none bg-black/30"
                          placeholder="Your company name"
                          data-testid="quote-company"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Contact Name *</label>
                        <Input
                          type="text"
                          name="contact_name"
                          value={formData.contact_name}
                          onChange={handleChange}
                          required
                          className="form-input h-13 rounded-none bg-black/30"
                          placeholder="Your full name"
                          data-testid="quote-name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="form-input h-13 rounded-none bg-black/30"
                          placeholder="email@company.com"
                          data-testid="quote-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Phone Number *</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="form-input h-13 rounded-none bg-black/30"
                          placeholder="+91 98765 43210"
                          data-testid="quote-phone"
                        />
                      </div>
                    </div>

                    <div className="pt-8 flex justify-end">
                      <Button
                        type="button"
                        onClick={() => setStep(2)}
                        disabled={!isStep1Valid}
                        className="btn-primary magnetic-btn clip-slant h-13 px-10"
                        data-testid="quote-next-1"
                      >
                        Continue <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Project Details */}
                {step === 2 && (
                  <div className="space-y-8 animate-fade-in" data-testid="quote-step-2">
                    <div className="mb-10">
                      <h2 className="font-heading text-2xl font-bold text-white uppercase mb-2">
                        Project Requirements
                      </h2>
                      <p className="text-gray-500">Tell us about your warehousing needs</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Industry *</label>
                        <Select
                          value={formData.industry}
                          onValueChange={(value) => handleSelectChange('industry', value)}
                        >
                          <SelectTrigger className="form-input h-13 rounded-none bg-black/30" data-testid="quote-industry">
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a1628] border-white/10">
                            {industries.map((industry) => (
                              <SelectItem key={industry} value={industry} className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5">
                                {industry}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Warehouse Size *</label>
                        <Select
                          value={formData.warehouse_size}
                          onValueChange={(value) => handleSelectChange('warehouse_size', value)}
                        >
                          <SelectTrigger className="form-input h-13 rounded-none bg-black/30" data-testid="quote-size">
                            <SelectValue placeholder="Select warehouse size" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a1628] border-white/10">
                            {warehouseSizes.map((size) => (
                              <SelectItem key={size} value={size} className="text-gray-300 hover:text-white focus:text-white focus:bg-white/5">
                                {size}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="pt-8 flex justify-between">
                      <Button
                        type="button"
                        onClick={() => setStep(1)}
                        className="btn-secondary h-13 px-8"
                        data-testid="quote-back-2"
                      >
                        <ArrowLeft className="mr-2 w-4 h-4" /> Back
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setStep(3)}
                        disabled={!isStep2Valid}
                        className="btn-primary magnetic-btn clip-slant h-13 px-10"
                        data-testid="quote-next-2"
                      >
                        Continue <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Products & Submit */}
                {step === 3 && (
                  <div className="space-y-8 animate-fade-in" data-testid="quote-step-3">
                    <div className="mb-10">
                      <h2 className="font-heading text-2xl font-bold text-white uppercase mb-2">
                        Products of Interest
                      </h2>
                      <p className="text-gray-500">Select the solutions you're interested in</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {products.map((product) => (
                        <label
                          key={product.id}
                          className={`flex items-center gap-4 p-4 border cursor-pointer transition-all duration-300 ${
                            formData.product_interest.includes(product.id)
                              ? 'border-[#E63946] bg-[#E63946]/10'
                              : 'border-white/10 hover:border-white/20 bg-black/20'
                          }`}
                          data-testid={`quote-product-${product.id}`}
                        >
                          <Checkbox
                            checked={formData.product_interest.includes(product.id)}
                            onCheckedChange={() => handleProductToggle(product.id)}
                            className="border-white/30 data-[state=checked]:bg-[#E63946] data-[state=checked]:border-[#E63946]"
                          />
                          <span className="text-gray-300 text-sm">{product.name}</span>
                        </label>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-3 uppercase tracking-wider">Additional Requirements</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="form-input rounded-none resize-none bg-black/30"
                        placeholder="Tell us more about your specific requirements, timeline, or any questions you have..."
                        data-testid="quote-message"
                      />
                    </div>

                    <div className="pt-8 flex justify-between">
                      <Button
                        type="button"
                        onClick={() => setStep(2)}
                        className="btn-secondary h-13 px-8"
                        data-testid="quote-back-3"
                      >
                        <ArrowLeft className="mr-2 w-4 h-4" /> Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-gold magnetic-btn clip-slant h-13 px-12"
                        data-testid="quote-submit"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: '24-48 Hour Response', desc: 'Quick turnaround on all inquiries' },
                { title: 'No Commitment', desc: 'Free consultation, no obligations' },
                { title: 'Expert Consultation', desc: 'Speak with industry specialists' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-white/5 border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-[#E63946] flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">{item.title}</p>
                    <p className="text-gray-600 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
